// src/pages/CreateContentTaskPage.js
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  collection, addDoc, serverTimestamp, getDocs, query, orderBy,
  writeBatch, doc, Timestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { useSocialLanguage } from '../hooks/useSocialLanguage';
import LanguageToggle from '../components/LanguageToggle';
import { getPlatformLabel, getContentTypeLabel } from '../utils/socialEnumLabels';
import styles from '../styles/CreateContentTaskPage.module.css';

// Roles
const managerRoles = ['Manager', 'Accounts Manager'];
const TRAINEE_ROLE_STRING = 'Trainee';

const PLATFORM_OPTIONS = ['Facebook', 'Instagram', 'TikTok', 'LinkedIn', 'X', 'YouTube', 'WhatsApp Channel'];
const CONTENT_TYPE_OPTIONS = ['Post', 'Reel', 'Video', 'Story', 'Carousel', 'Cover', 'Ad Creative', 'Campaign Content'];

const TRANSLATIONS = {
  en: {
    pageTitle: 'Create New Content Task',
    loadingFormData: 'Loading form data...',
    client: 'Client',
    searchClients: 'Search Clients...',
    noMatchingClients: 'No matching clients',
    selectOneClient: 'Select one client',
    platform: 'Platforms',
    platformsHint: 'Select one or more platforms to publish this content to.',
    contentType: 'Content Type',
    dueDate: 'Due Date',
    linkedPage: 'Linked Social Page (optional, required for Meta auto-publishing)',
    noPageLinked: '— No linked page —',
    noPagesForClientPlatform: 'No social pages found for this client/platform. Add one on the Client Social Pages page first.',
    errSelectPlatform: 'Please select at least one platform.',
    title: 'Title',
    titlePlaceholder: 'e.g., Summer Sale Announcement Post',
    caption: 'Caption',
    captionPlaceholder: 'Write the caption / copy here...',
    hashtags: 'Hashtags',
    hashtagsPlaceholder: '#brand #marketing',
    cta: 'Call To Action (optional)',
    ctaPlaceholder: 'e.g., Shop now via the link in bio',
    assignTo: 'Assign To (Employees only)',
    searchEmployees: 'Search Employees...',
    noMatchingEmployees: 'No matching employees',
    holdCtrlCmd: 'Hold Ctrl/Cmd to select multiple',
    writerOptional: 'Writer (optional)',
    designerOptional: 'Designer (optional)',
    reviewerOptional: 'Reviewer (optional)',
    none: '— None —',
    cancel: 'Cancel',
    createContentTask: 'Create Content Task',
    errAuth: 'Authentication error.',
    errSelectClient: 'Please select a Client.',
    errTitleRequired: 'Title is required.',
    errDueDateRequired: 'Due Date is required.',
    errAssignAtLeastOne: 'Please assign the content task to at least one person.',
    errNotifyStep: 'Content task created, but failed during notification step.',
    errCreateFailed: 'Failed to create content task.',
    successCreated: (taskTitle) => `Content task "${taskTitle}" created successfully!`,
  },
  ar: {
    pageTitle: 'إنشاء مهمة محتوى جديدة',
    loadingFormData: 'جارٍ تحميل بيانات النموذج...',
    client: 'العميل',
    searchClients: 'البحث عن عملاء...',
    noMatchingClients: 'لا يوجد عملاء مطابقون',
    selectOneClient: 'اختر عميلاً واحداً',
    platform: 'المنصات',
    platformsHint: 'اختر منصة واحدة أو أكثر لنشر هذا المحتوى عليها.',
    contentType: 'نوع المحتوى',
    dueDate: 'تاريخ الاستحقاق',
    linkedPage: 'الصفحة المرتبطة (اختياري، مطلوب للنشر التلقائي عبر ميتا)',
    noPageLinked: '— بدون صفحة مرتبطة —',
    noPagesForClientPlatform: 'لا توجد صفحات سوشيال ميديا لهذا العميل/المنصة. أضف صفحة من صفحة "صفحات السوشيال ميديا للعملاء" أولاً.',
    errSelectPlatform: 'يرجى اختيار منصة واحدة على الأقل.',
    title: 'العنوان',
    titlePlaceholder: 'مثال: منشور الإعلان عن تخفيضات الصيف',
    caption: 'النص المصاحب',
    captionPlaceholder: 'اكتب النص المصاحب / المحتوى هنا...',
    hashtags: 'الوسوم',
    hashtagsPlaceholder: '#براند #تسويق',
    cta: 'دعوة لاتخاذ إجراء (اختياري)',
    ctaPlaceholder: 'مثال: تسوق الآن عبر الرابط في الملف الشخصي',
    assignTo: 'تعيين إلى (الموظفون فقط)',
    searchEmployees: 'البحث عن موظفين...',
    noMatchingEmployees: 'لا يوجد موظفون مطابقون',
    holdCtrlCmd: 'اضغط مع الاستمرار على Ctrl/Cmd لتحديد أكثر من عنصر',
    writerOptional: 'الكاتب (اختياري)',
    designerOptional: 'المصمم (اختياري)',
    reviewerOptional: 'المراجع (اختياري)',
    none: '— لا أحد —',
    cancel: 'إلغاء',
    createContentTask: 'إنشاء مهمة المحتوى',
    errAuth: 'خطأ في المصادقة.',
    errSelectClient: 'يرجى اختيار عميل.',
    errTitleRequired: 'العنوان مطلوب.',
    errDueDateRequired: 'تاريخ الاستحقاق مطلوب.',
    errAssignAtLeastOne: 'يرجى تعيين مهمة المحتوى لشخص واحد على الأقل.',
    errNotifyStep: 'تم إنشاء مهمة المحتوى، ولكن حدث خطأ خلال خطوة الإشعار.',
    errCreateFailed: 'فشل إنشاء مهمة المحتوى.',
    successCreated: (taskTitle) => `تم إنشاء مهمة المحتوى "${taskTitle}" بنجاح!`,
  },
};

const isFiredOrInactive = (user) => {
  const inactiveFlag = user?.active === false || user?.isActive === false;
  const firedFlag = user?.isFired === true;

  const statusStr = String(user?.employmentStatus || user?.status || '').toLowerCase();
  const statusFlag = ['fired', 'terminated', 'inactive', 'disabled'].includes(statusStr);

  const endDate = user?.employmentEndDate ? new Date(user.employmentEndDate) : null;
  const endDateFlag = endDate && !isNaN(endDate.getTime()) ? endDate.getTime() <= Date.now() : false;

  return inactiveFlag || firedFlag || statusFlag || endDateFlag;
};

const CreateContentTaskPage = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { language, toggleLanguage, isRTL } = useSocialLanguage();
  const t = TRANSLATIONS[language];

  // --- Form State ---
  const [clientId, setClientId] = useState('');
  const [selectedPlatforms, setSelectedPlatforms] = useState([PLATFORM_OPTIONS[0]]);
  const [pageSelections, setPageSelections] = useState({}); // { [platform]: pageId }
  const [contentType, setContentType] = useState(CONTENT_TYPE_OPTIONS[0]);
  const [title, setTitle] = useState('');
  const [caption, setCaption] = useState('');
  const [hashtags, setHashtags] = useState('');
  const [cta, setCta] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [assignedTo, setAssignedTo] = useState([]); // Array of UIDs
  const [writerId, setWriterId] = useState('');
  const [designerId, setDesignerId] = useState('');
  const [reviewerId, setReviewerId] = useState('');

  // --- Helper State ---
  const [employees, setEmployees] = useState([]); // assignable (excludes trainees + fired/inactive)
  const [clients, setClients] = useState([]);      // { id, name }
  const [socialPages, setSocialPages] = useState([]); // { id, clientId, platform, pageName }
  const [managerUids, setManagerUids] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [assigneeSearchTerm, setAssigneeSearchTerm] = useState('');

  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;

    const fetchInitialData = async () => {
      if (!isMounted.current) return;
      setFormLoading(true);
      setError('');
      try {
        // ---- Users ----
        const usersCol = collection(db, 'users');
        const qAll = query(usersCol, orderBy('name', 'asc'));
        const allUsersSnapshot = await getDocs(qAll);
        if (!isMounted.current) return;

        const allUsersList = allUsersSnapshot.docs.map(d => ({
          id: d.id,
          ...d.data(),
        }));

        // Exclude trainees + fired/inactive
        const employeeList = allUsersList
          .filter(u => u.role !== TRAINEE_ROLE_STRING && !isFiredOrInactive(u))
          .map(u => ({
            id: u.id,
            name: u.name || u.email || u.id,
            email: u.email || '',
          }));
        setEmployees(employeeList);

        // Managers
        const managerIdList = allUsersList
          .filter(u => managerRoles.includes(u.role))
          .map(u => u.id);
        setManagerUids(managerIdList);

        // ---- Clients ----
        const clientsRef = collection(db, 'clients');
        const qClients = query(clientsRef, orderBy('name', 'asc'));
        const clientsSnapshot = await getDocs(qClients);
        if (!isMounted.current) return;

        const clientList = clientsSnapshot.docs.map(d => ({
          id: d.id,
          name: d.data().name,
        }));
        setClients(clientList);

        // ---- Social Pages (for the optional Linked Social Page selector) ----
        const pagesSnapshot = await getDocs(collection(db, 'socialPages'));
        if (!isMounted.current) return;
        setSocialPages(pagesSnapshot.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error('Error fetching initial data:', err);
        if (isMounted.current) {
          setError(prev => prev || 'Could not load dropdown data.');
          setEmployees([]);
          setManagerUids([]);
          setClients([]);
          setSocialPages([]);
        }
      } finally {
        if (isMounted.current) setFormLoading(false);
      }
    };

    fetchInitialData();
    return () => { isMounted.current = false; };
  }, []);

  // Filtered dropdown lists
  const filteredClients = useMemo(() => {
    if (!clientSearchTerm) return clients;
    return clients.filter(c =>
      (c.name || '').toLowerCase().includes(clientSearchTerm.toLowerCase())
    );
  }, [clients, clientSearchTerm]);

  const pagesForPlatform = useMemo(() => {
    const map = {};
    PLATFORM_OPTIONS.forEach(p => {
      map[p] = clientId ? socialPages.filter(sp => sp.clientId === clientId && sp.platform === p) : [];
    });
    return map;
  }, [socialPages, clientId]);

  const togglePlatform = (p) => {
    setSelectedPlatforms(prev => prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]);
  };

  // Keep pageSelections in sync: drop selections for unchecked/invalid platforms, and
  // auto-pick the page when a platform has exactly one social page for this client.
  useEffect(() => {
    setPageSelections(prev => {
      const next = {};
      selectedPlatforms.forEach(p => {
        const pages = pagesForPlatform[p] || [];
        const current = prev[p];
        if (current && pages.some(pg => pg.id === current)) {
          next[p] = current;
        } else if (pages.length === 1) {
          next[p] = pages[0].id;
        }
      });
      return next;
    });
  }, [selectedPlatforms, pagesForPlatform]);

  const filteredEmployees = useMemo(() => {
    if (!assigneeSearchTerm) return employees;
    return employees.filter(emp =>
      (emp.name || '').toLowerCase().includes(assigneeSearchTerm.toLowerCase())
    );
  }, [employees, assigneeSearchTerm]);

  // Multi-select handler
  const handleAssigneeChange = (e) => {
    const { options } = e.target;
    const selected = [];
    for (let i = 0; i < options.length; i++) {
      if (options[i].selected) selected.push(options[i].value);
    }
    setAssignedTo(selected);
  };

  // ---------- Submit ----------
  const handleSubmit = async (e) => {
    e.preventDefault();

    // ── Validation ────────────────────────────────────────────────────────
    if (!currentUser) { setError(t.errAuth); return; }
    if (!clientId) { setError(t.errSelectClient); return; }
    if (selectedPlatforms.length === 0) { setError(t.errSelectPlatform); return; }
    if (!title.trim()) { setError(t.errTitleRequired); return; }
    if (!dueDate) { setError(t.errDueDateRequired); return; }
    if (assignedTo.length === 0) {
      setError(t.errAssignAtLeastOne);
      return;
    }

    setError('');
    setLoading(true);
    setSuccessMessage('');
    let createdTaskId = null;
    let taskCreationFailed = false;

    const selectedClient = clients.find(c => c.id === clientId);
    const clientName = selectedClient?.name || 'Unknown Client';
    const targets = selectedPlatforms.map(p => {
      const page = (pagesForPlatform[p] || []).find(pg => pg.id === pageSelections[p]);
      return { platform: p, pageId: page?.id || '', pageName: page?.pageName || '' };
    });

    try {
      // visibleTo: creator + assignees + writer/designer/reviewer + managers
      const visibilitySet = new Set();
      if (currentUser.uid) visibilitySet.add(currentUser.uid);
      assignedTo.forEach(uid => visibilitySet.add(uid));
      if (writerId) visibilitySet.add(writerId);
      if (designerId) visibilitySet.add(designerId);
      if (reviewerId) visibilitySet.add(reviewerId);
      managerUids.forEach(uid => visibilitySet.add(uid));
      const visibleToArray = Array.from(visibilitySet);

      const contentTasksCol = collection(db, 'contentTasks');
      const contentTaskData = {
        clientId,
        clientName,
        targets,
        platforms: selectedPlatforms,
        platform: selectedPlatforms.join(' + '),
        pageId: targets[0]?.pageId || '',
        pageName: targets[0]?.pageName || '',
        contentType,
        title: title.trim(),
        caption: caption.trim(),
        hashtags: hashtags.trim(),
        cta: cta.trim(),
        status: 'New Request',
        assignedTo,
        writerId: writerId || null,
        designerId: designerId || null,
        reviewerId: reviewerId || null,
        dueDate: Timestamp.fromDate(new Date(dueDate + 'T00:00:00')),
        scheduledDate: null,
        publishedDate: null,
        mediaUrls: [],
        postUrl: '',
        postUrls: [],
        clientApprovalStatus: null,
        clientNotes: '',
        internalNotes: '',
        visibleTo: visibleToArray,
        createdBy: currentUser.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      const docRef = await addDoc(contentTasksCol, contentTaskData);
      createdTaskId = docRef.id;

      // ── Notify assignees + writer/designer/reviewer (in-app) ────────────
      const notifyUids = new Set();
      assignedTo.forEach(uid => notifyUids.add(uid));
      if (writerId) notifyUids.add(writerId);
      if (designerId) notifyUids.add(designerId);
      if (reviewerId) notifyUids.add(reviewerId);
      notifyUids.delete(currentUser.uid);

      if (notifyUids.size > 0) {
        const batch = writeBatch(db);
        const notificationsCol = collection(db, 'notifications');
        const creatorName = currentUser.displayName || currentUser.email || 'Someone';

        notifyUids.forEach(notifyUid => {
          const notificationRef = doc(notificationsCol);
          batch.set(notificationRef, {
            userId: notifyUid,
            contentTaskId: createdTaskId,
            taskName: contentTaskData.title,
            message: `${creatorName} assigned you a new content task: "${contentTaskData.title}" for ${clientName}`,
            timestamp: serverTimestamp(),
            isRead: false,
            createdBy: currentUser.uid,
            createdByName: creatorName,
            type: 'content_task_assignment',
          });
        });

        await batch.commit();
      }

      setSuccessMessage(t.successCreated(contentTaskData.title));
      setTimeout(() => {
        navigate(`/social/tasks/${createdTaskId}`, {
          state: { message: t.successCreated(contentTaskData.title) }
        });
      }, 1000);

    } catch (err) {
      console.error('Error during content task creation/notification:', err);
      if (createdTaskId) {
        setError(t.errNotifyStep);
      } else {
        setError(t.errCreateFailed);
        taskCreationFailed = true;
      }
    } finally {
      if (taskCreationFailed) setLoading(false);
    }
  };

  return (
    <div className={styles.pageContainer} dir={isRTL ? 'rtl' : 'ltr'}>
      <div className={styles.formBox}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' }}>
          <h2 className={styles.formTitle}>{t.pageTitle}</h2>
          <LanguageToggle language={language} onToggle={toggleLanguage} />
        </div>
        {error && <div className={styles.errorMessage}>{error}</div>}
        {successMessage && <div className={styles.successMessage}>{successMessage}</div>}

        {formLoading ? (
          <div className={styles.loadingIndicator}>{t.loadingFormData}</div>
        ) : (
          <form onSubmit={handleSubmit} className={styles.form}>
            <div className={styles.inputGroup}>
              <label htmlFor="client">{t.client}</label>
              <input
                type="text"
                placeholder={t.searchClients}
                value={clientSearchTerm}
                onChange={(e) => setClientSearchTerm(e.target.value)}
                disabled={loading || formLoading || clients.length === 0}
                className={styles.searchInput}
              />
              <select
                id="client"
                value={clientId}
                onChange={(e) => { setClientId(e.target.value); setError(''); }}
                required
                disabled={loading || formLoading || clients.length === 0}
              >
                <option value="">{t.selectOneClient}</option>
                {filteredClients.length === 0 && !formLoading && (
                  <option disabled>{t.noMatchingClients}</option>
                )}
                {filteredClients.map(client => (
                  <option key={client.id} value={client.id}>{client.name}</option>
                ))}
              </select>
              <small className={styles.selectHelpText}>{t.selectOneClient}</small>
            </div>

            <div className={styles.inputGroup}>
              <label>{t.platform}</label>
              <small className={styles.selectHelpText}>{t.platformsHint}</small>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', marginTop: '0.4rem' }}>
                {PLATFORM_OPTIONS.map(p => {
                  const pages = pagesForPlatform[p] || [];
                  const checked = selectedPlatforms.includes(p);
                  return (
                    <div key={p} style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.6rem' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontWeight: 'normal', minWidth: '9rem' }}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => togglePlatform(p)}
                          disabled={loading}
                        />
                        {getPlatformLabel(p, language)}
                      </label>
                      {checked && (
                        <select
                          value={pageSelections[p] || ''}
                          onChange={(e) => setPageSelections(prev => ({ ...prev, [p]: e.target.value }))}
                          disabled={loading || !clientId || pages.length === 0}
                          style={{ flex: '1 1 auto', minWidth: '12rem' }}
                        >
                          <option value="">{t.noPageLinked}</option>
                          {pages.map(page => (
                            <option key={page.id} value={page.id}>{page.pageName}</option>
                          ))}
                        </select>
                      )}
                      {checked && clientId && pages.length === 0 && (
                        <small className={styles.selectHelpText}>{t.noPagesForClientPlatform}</small>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className={styles.gridThreeCols}>
              <div className={styles.inputGroup}>
                <label htmlFor="contentType">{t.contentType}</label>
                <select
                  id="contentType"
                  value={contentType}
                  onChange={(e) => setContentType(e.target.value)}
                  disabled={loading}
                >
                  {CONTENT_TYPE_OPTIONS.map(ct => (
                    <option key={ct} value={ct}>{getContentTypeLabel(ct, language)}</option>
                  ))}
                </select>
              </div>
              <div className={styles.inputGroup}>
                <label htmlFor="dueDate">{t.dueDate}</label>
                <input
                  type="date"
                  id="dueDate"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  required
                  disabled={loading}
                />
              </div>
            </div>

            <div className={styles.inputGroup}>
              <label htmlFor="title">{t.title}</label>
              <input
                type="text"
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                disabled={loading}
                placeholder={t.titlePlaceholder}
              />
            </div>

            <div className={styles.inputGroup}>
              <label htmlFor="caption">{t.caption}</label>
              <textarea
                id="caption"
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                rows={5}
                disabled={loading}
                placeholder={t.captionPlaceholder}
              />
            </div>

            <div className={styles.gridThreeCols}>
              <div className={styles.inputGroup}>
                <label htmlFor="hashtags">{t.hashtags}</label>
                <input
                  type="text"
                  id="hashtags"
                  value={hashtags}
                  onChange={(e) => setHashtags(e.target.value)}
                  disabled={loading}
                  placeholder={t.hashtagsPlaceholder}
                />
              </div>
              <div className={`${styles.inputGroup} ${styles.spanTwoCols}`}>
                <label htmlFor="cta">{t.cta}</label>
                <input
                  type="text"
                  id="cta"
                  value={cta}
                  onChange={(e) => setCta(e.target.value)}
                  disabled={loading}
                  placeholder={t.ctaPlaceholder}
                />
              </div>
            </div>

            <div className={styles.inputGroup}>
              <label htmlFor="assignedTo">{t.assignTo}</label>
              <input
                type="text"
                placeholder={t.searchEmployees}
                value={assigneeSearchTerm}
                onChange={(e) => setAssigneeSearchTerm(e.target.value)}
                disabled={loading || formLoading || employees.length === 0}
                className={styles.searchInput}
              />
              <select
                id="assignedTo"
                multiple
                value={assignedTo}
                onChange={handleAssigneeChange}
                required
                size="6"
                disabled={loading || formLoading || employees.length === 0}
                className={styles.multiSelectInput}
              >
                {filteredEmployees.length === 0 && !formLoading && (
                  <option disabled>{t.noMatchingEmployees}</option>
                )}
                {filteredEmployees.map(emp => (
                  <option key={emp.id} value={emp.id}>{emp.name}</option>
                ))}
              </select>
              <small className={styles.selectHelpText}>{t.holdCtrlCmd}</small>
            </div>

            <div className={styles.gridThreeCols}>
              <div className={styles.inputGroup}>
                <label htmlFor="writerId">{t.writerOptional}</label>
                <select
                  id="writerId"
                  value={writerId}
                  onChange={(e) => setWriterId(e.target.value)}
                  disabled={loading || formLoading || employees.length === 0}
                >
                  <option value="">{t.none}</option>
                  {employees.map(emp => (
                    <option key={emp.id} value={emp.id}>{emp.name}</option>
                  ))}
                </select>
              </div>
              <div className={styles.inputGroup}>
                <label htmlFor="designerId">{t.designerOptional}</label>
                <select
                  id="designerId"
                  value={designerId}
                  onChange={(e) => setDesignerId(e.target.value)}
                  disabled={loading || formLoading || employees.length === 0}
                >
                  <option value="">{t.none}</option>
                  {employees.map(emp => (
                    <option key={emp.id} value={emp.id}>{emp.name}</option>
                  ))}
                </select>
              </div>
              <div className={styles.inputGroup}>
                <label htmlFor="reviewerId">{t.reviewerOptional}</label>
                <select
                  id="reviewerId"
                  value={reviewerId}
                  onChange={(e) => setReviewerId(e.target.value)}
                  disabled={loading || formLoading || employees.length === 0}
                >
                  <option value="">{t.none}</option>
                  {employees.map(emp => (
                    <option key={emp.id} value={emp.id}>{emp.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className={styles.buttonGroup}>
              <button
                type="button"
                className={`${styles.formButton} ${styles.cancelButton}`}
                onClick={() => navigate(-1)}
                disabled={loading}
              >
                {t.cancel}
              </button>
              <button
                type="submit"
                className={`${styles.formButton} ${styles.submitButton}`}
                disabled={loading || formLoading}
              >
                {loading ? <span className={styles.loadingSpinner}></span> : t.createContentTask}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default CreateContentTaskPage;
